import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    postScraperExtract,
    getScraperProgress,
    sendScraperCSVToEmail,
    getScraperCampaignSummaries,
    postStartColdOutreach,
    getColdOutreachStatus
} from '../services/api';

// Start scraper and get job_id
export const extractData = createAsyncThunk(
    'scraper/extractData',
    async ({ url, pages, campaign_name, category_ids, subcategory_ids }, { dispatch }) => {
        const response = await postScraperExtract(url, pages, campaign_name, category_ids, subcategory_ids);
        const { job_id } = response.data;

        // Start polling
        dispatch(pollScraperProgress(job_id));

        return response.data;
    }
);

// Poll progress
export const pollScraperProgress = createAsyncThunk(
    'scraper/pollProgress',
    async (job_id, { dispatch }) => {
        const pollInterval = 2000;

        return new Promise((resolve) => {
            const interval = setInterval(async () => {
                const progress = await getScraperProgress(job_id);

                dispatch(setProgress(progress.data));

                if (progress.data.status === 'COMPLETED' || progress.data.status === 'FAILED') {
                    clearInterval(interval);
                    resolve(progress.data);
                }
            }, pollInterval);
        });
    }
);

// Fetch scraper campaign summaries
export const fetchScraperCampaignSummaries = createAsyncThunk(
    'scraper/fetchScraperCampaignSummaries',
    async () => {
        const data = await getScraperCampaignSummaries();
        return data;
    }
);

// Send CSV to email
export const emailCSV = createAsyncThunk(
    'scraper/emailCSV',
    async ({ type, website, campaign }, { rejectWithValue }) => {
        try {
            const data = await sendScraperCSVToEmail(type, website, campaign);
            return { data, message: 'CSV has been sent to the admin successfully.' };
        } catch (error) {
            return rejectWithValue('Failed to send CSV to admin.');
        }
    }
);

// Start cold outreach campaign
export const startColdOutreach = createAsyncThunk(
    'scraper/startColdOutreach',
    async ({ outreach_email_subject, outreach_email_html, outreach_email_keys, targeted_segment, attachments }, { rejectWithValue }) => {
        try {
            const data = await postStartColdOutreach(outreach_email_subject, outreach_email_html, outreach_email_keys, targeted_segment, attachments);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Cold outreach failed.');
        }
    }
);

// Track cold outreach progress
export const trackColdOutreach = createAsyncThunk(
    'scraper/trackColdOutreach',
    async (job_id, { rejectWithValue }) => {
        try {
            const data = await getColdOutreachStatus(job_id);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Tracking cold outreach failed.');
        }
    }
);

const scraperSlice = createSlice({
    name: 'scraper',
    initialState: {
        loading: false,            // used ONLY for extractData / polling
        summariesLoading: false,   // used ONLY for fetchScraperCampaignSummaries
        emailing: null,
        error: null,
        progress: null,
        scrapingCampaignSummaries: [],
        emailStatusMessage: null,
        coldOutreachStatus: null,
        coldOutreachLoading: false,
        coldOutreachError: null,
    },
    reducers: {
        setProgress: (state, action) => {
            state.progress = action.payload;
        },
        resetEmailStatusMessage: (state) => {
            state.emailStatusMessage = null;
        },
    },
    extraReducers: (builder) => {
        builder

            // ── extractData ──────────────────────────────────────────────
            .addCase(extractData.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.progress = null;
            })
            .addCase(extractData.fulfilled, (state) => {
                // Stop the "Extracting…" spinner — ScraperLoader with progress
                // prop takes over from here as polling fires setProgress updates
                state.loading = false;
            })
            .addCase(extractData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })

            // ── emailCSV ─────────────────────────────────────────────────
            .addCase(emailCSV.pending, (state) => {
                state.emailing = true;
            })
            .addCase(emailCSV.fulfilled, (state, action) => {
                state.emailing = false;
                state.emailStatusMessage = action.payload.message;
            })
            .addCase(emailCSV.rejected, (state, action) => {
                state.emailing = false;
                state.error = action.error.message;
                state.emailStatusMessage = action.payload || 'An error occurred while sending CSV.';
            })

            // ── fetchScraperCampaignSummaries ────────────────────────────
            // Uses summariesLoading — completely separate from extractData
            .addCase(fetchScraperCampaignSummaries.pending, (state) => {
                state.summariesLoading = true;
                state.error = null;
            })
            .addCase(fetchScraperCampaignSummaries.fulfilled, (state, action) => {
                state.summariesLoading = false;
                state.campaignSummaries = action.payload;
                state.scrapingCampaignSummaries = action.payload;
            })
            .addCase(fetchScraperCampaignSummaries.rejected, (state, action) => {
                state.summariesLoading = false;
                state.error = action.error.message;
            })

            // ── startColdOutreach ────────────────────────────────────────
            .addCase(startColdOutreach.pending, (state) => {
                state.coldOutreachLoading = true;
                state.coldOutreachError = null;
            })
            .addCase(startColdOutreach.fulfilled, (state, action) => {
                state.coldOutreachLoading = false;
                state.coldOutreachStatus = action.payload;
            })
            .addCase(startColdOutreach.rejected, (state, action) => {
                state.coldOutreachLoading = false;
                state.coldOutreachError = action.payload;
            })

            // ── trackColdOutreach ────────────────────────────────────────
            .addCase(trackColdOutreach.fulfilled, (state, action) => {
                state.coldOutreachStatus = action.payload;
            })
            .addCase(trackColdOutreach.rejected, (state, action) => {
                state.coldOutreachError = action.payload;
            });
    },
});

export const { setProgress, resetEmailStatusMessage } = scraperSlice.actions;
export default scraperSlice.reducer;